import type { SessionSummary } from '@perplexicon/api-client';

import { resolveGameCatalogArt } from '../assets/asset-manifest.js';
import { GAME_CATALOG_ART } from '../games/registry.js';
import { formatDateTime } from '../lib/format-date.js';
import { opponentNames, roundLabel, turnLabel } from '../lib/session-display.js';
import { ActionLink } from './ActionLink.js';
import { Card } from './Card.js';
import { StatusChip } from './StatusChip.js';

export interface ActiveGameCardProps {
  entry: SessionSummary;
  /** The viewer's own user id, used to render "opponent"/"whose turn" from their perspective. */
  selfId: string;
}

/**
 * A single in-progress game (APP_SPEC §18's active-games fields: game name,
 * opponent, current state summary, whose turn when applicable, current
 * round when applicable, last activity), with one explicit control back into
 * it. Every field comes from the platform's own `SessionSummary` — no
 * game-specific rendering exists here (CLAUDE.md §2). Used by the
 * dashboard's "Continue playing" section.
 *
 * The card is a container, not a link: "Continue" is a real link with the
 * game named in its accessible name, so a screen-reader user moving by link
 * hears which game each one resumes, and the row's own text stays selectable.
 */
export function ActiveGameCard({ entry, selfId }: ActiveGameCardProps) {
  const turn = turnLabel(entry, selfId);
  const round = roundLabel(entry);
  const yourTurn = entry.currentTurnUserId === selfId;

  return (
    <Card className="flex flex-col gap-3 p-3 tablet:flex-row tablet:items-center tablet:gap-4">
      {/* A ~80px thumbnail never needs the 2x rendition. */}
      <img
        src={resolveGameCatalogArt(GAME_CATALOG_ART[entry.gameId]).src}
        width={640}
        height={360}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="hidden h-12 w-20 shrink-0 rounded-md border border-border object-cover tablet:block"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate font-display font-semibold text-textPrimary">{entry.gameName}</p>
          {yourTurn && <StatusChip tone="active">Your turn</StatusChip>}
        </div>
        <p className="truncate text-sm text-textSecondary">
          vs {opponentNames(entry, selfId)}
          {round && <span className="text-textMuted"> · {round}</span>}
        </p>
        {/* The chip already says "Your turn"; the line below it then carries
            the game's own state summary rather than repeating the chip. */}
        <p className="truncate text-sm text-textSecondary">
          {yourTurn ? entry.summary.headline : (turn ?? entry.summary.headline)}
        </p>
      </div>
      <div className="flex shrink-0 items-center justify-between gap-3 tablet:flex-col tablet:items-end tablet:gap-1">
        <ActionLink to={`/play/${entry.id}`} variant="primary" emphasis="decorated">
          Continue
          <span className="sr-only"> — {entry.gameName}</span>
        </ActionLink>
        <p className="text-xs text-textMuted">{formatDateTime(entry.lastActionAt)}</p>
      </div>
    </Card>
  );
}
